import React, { Component } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import { ColumnProps } from 'primereact/column';
import moment from 'moment';
import CustomTable from '../../custom/CustomTable';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CollectionNames from '../../../constant/CollectionNames';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import WatchlistService from '../../../service/WatchlistService';
import LanguageUtil from '../../../util/LanguageUtil';
import { MatchManyLogCount, MatchManyLogSummary } from '../../../domain/watchlist/WatchlistHistoryDomains';
import NavigationUtil from '../../../util/NavigationUtil';
import ItemToolsMenu, { ItemToolsMenuCustomCount } from '../../ItemToolsMenu';
import FilterUtil from '../../../util/FilterUtil';
import ValueFactory from '../../../util/ValueFactory';
import { dispatch } from '../../../redux/store';
import { changePageFilter } from '../../../redux/actions/PageActions';
import JobService from '../../../service/JobService';
import { MatchManyLogExportJob } from '../../../domain/job/JobDomain';
import MessageUtil from '../../../util/MessageUtil';
import RoleHelper from '../../../RoleHelper';
import WatchlistHistorySettingsComponent from './WatchlistHistorySettingsComponent';
import _ from 'lodash';
import QueryBuilder = query.QueryBuilder;
import Filter = query.Filter;
import empty = query.empty;

const SUMMARY_FIELDS: (keyof MatchManyLogSummary)[] = [
  'id',
  'searchedName',
  'matchType',
  'referenceId',
  'createdAt',
  'threshold',
  'maxScore',
  'resultCount',
  'loggedCount',
  'truncated',
];

class WatchlistHistoryComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private watchlistService: WatchlistService = ServiceContainer.getInstance().get(ServiceNames.WATCHLIST);
  private jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);
  private logSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private countRequestId: number = 0;

  public render() {
    const { logs, rows, currentPage, totalRecord, loading, filter, settingsVisible } = this.state;
    return (
      <div className={'w-full h-full flex flex-col px-5'}>
        <div className={'flex justify-between bg-white items-center w-full'}>
          <ItemToolsMenu
            views={false}
            countVisible={true}
            totalCount={totalRecord}
            customCounts={this.getTypeCounts()}
            importVisible={false}
            exportVisible={logs.length > 0}
            exportVisibleAlways={true}
            exportFunc={this.exportLogs}
            settingsFunc={RoleHelper.isAdmin() ? () => this.setState({ settingsVisible: true }) : undefined}
            componentLabel={LanguageUtil.get({ id: 'common.tabs.history', defaultMessage: 'History' })}
            componentId={'watchlist-history'}
            onChangeFilter={this.onChangeFilter}
            filter={filter}
            defaultFilterDisabled={true}
          />
        </div>
        <div>
          <div className={'flex bg-white w-full overflow-y-scroll h-[calc(100vh-400px)] 2xl:h-[calc(100vh-450px)]'}>
            <CustomTable columns={this.getColumns()} value={logs} onRowClick={(e) => this.onRowClick(e.data)} />
          </div>
          <CustomPaginator
            currentPage={currentPage}
            key='watchlist-history'
            rows={rows}
            totalRecords={totalRecord}
            loading={loading}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            onPageChange={this.onPageChange}
          />
        </div>
        <WatchlistHistorySettingsComponent visible={settingsVisible} onHide={() => this.setState({ settingsVisible: false })} />
      </div>
    );
  }

  public componentDidMount() {
    this.prepareFilterFields();
    this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private prepareFilterFields = () => {
    dispatch(
      changePageFilter({
        filter: ValueFactory.initializeFilter(),
        items: FilterUtil.getWatchlistHistoryFields(),
      }),
    );
  };

  private startTracker = () => {
    const { rows, currentPage, filter } = this.state;
    const logQuery: query.Query = QueryBuilder.select()
      .select(...SUMMARY_FIELDS)
      .from(CollectionNames.MATCH_MANY_LOGS)
      .where(filter)
      .orderBy({ field: 'createdAt', operator: 'DESC' })
      .limit(rows)
      .skip((currentPage - 1) * rows)
      .build();
    this.logSubscription = subscribe.Formica.subscribe(CollectionNames.MATCH_MANY_LOGS, logQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.logSubscription]);
  };

  private stopTracker = async () => {
    await this.logSubscription?.stop();
    this.computation?.stop();
  };

  private reSubscribe = async () => {
    await this.stopTracker();
    this.startTracker();
  };

  private tracker = async () => {
    const { rows } = this.state;
    if (!this.logSubscription?.ready()) {
      return;
    }
    const logQuery: query.Query = QueryBuilder.select()
      .from(CollectionNames.MATCH_MANY_LOGS)
      .orderBy({ field: 'createdAt', operator: 'DESC' })
      .limit(rows)
      .build();
    const logs: MatchManyLogSummary[] = await this.repository.findMany(logQuery);
    this.setState({ logs, loading: false });
    this.prepareLogCount();
  };

  private prepareLogCount = async () => {
    const requestId = ++this.countRequestId;
    try {
      const count: MatchManyLogCount = await this.watchlistService.getMatchManyLogCount(this.state.filter);
      if (requestId == this.countRequestId) {
        this.setState({ totalRecord: count.total, count });
      }
    } catch (e) {}
  };

  private getTypeCounts = (): ItemToolsMenuCustomCount[] => {
    const { count } = this.state;
    if (count == undefined) {
      return [];
    }
    return [
      {
        key: 'individual',
        label: LanguageUtil.get({ id: 'common.tabs.individual', defaultMessage: 'Individual' }),
        value: count.individual,
        className: 'bg-[#EFF8FF] text-[#175CD3]',
      },
      {
        key: 'entity',
        label: LanguageUtil.get({ id: 'common.tabs.entity', defaultMessage: 'Entity' }),
        value: count.entity,
        className: 'bg-[#FCF1BB] text-[#C57500]',
      },
      {
        key: 'general',
        label: LanguageUtil.get({ id: 'common.tabs.general', defaultMessage: 'General' }),
        value: count.general,
        className: 'bg-[#F2F4F7] text-[#4F4F4F]',
      },
    ];
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page, loading: true }, this.reSubscribe);
  };

  private onChangeFilter = (filter: Filter) => {
    if (_.isEqual(filter, this.state.filter)) {
      return;
    }
    this.setState({ filter, currentPage: 1, loading: true }, this.reSubscribe);
  };

  private exportLogs = async () => {
    const job: MatchManyLogExportJob = {
      type: 'MATCH_MANY_LOG_EXPORT',
      timestamp: moment().valueOf(),
      status: 'PENDING',
      filter: this.state.filter,
      extension: 'csv',
      path: '',
    };
    await this.jobService.schedule(job);
    MessageUtil.success({
      summary: 'Export',
      detail: <div>{LanguageUtil.get({ id: 'pages.alert.exportJobScheduled', defaultMessage: 'Export job scheduled.' })}</div>,
    });
  };

  private onRowClick = (log: MatchManyLogSummary) => {
    window.open(`/${NavigationUtil.getTenant()}/watchlist/history/${encodeURIComponent(log.id)}`, '_blank', 'noopener noreferrer');
  };

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'referenceId',
        className: 'min-w-[200px]',
        header: LanguageUtil.get({ id: 'common.label.referenceId', defaultMessage: 'Reference ID' }),
        body: (record: MatchManyLogSummary) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.referenceId ?? ''} />
            </div>
          );
        },
      },
      {
        field: 'matchType',
        className: 'min-w-[150px]',
        header: LanguageUtil.get({ id: 'common.label.type', defaultMessage: 'Type' }),
        body: (record: MatchManyLogSummary) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.matchType} />
            </div>
          );
        },
      },
      {
        field: 'searchedName',
        className: 'min-w-[250px]',
        header: LanguageUtil.get({ id: 'pages.watchlist.history.searchedName', defaultMessage: 'Searched Name' }),
        body: (record: MatchManyLogSummary) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.searchedName ?? ''} />
            </div>
          );
        },
      },
      {
        field: 'threshold',
        className: 'min-w-[130px]',
        header: LanguageUtil.get({ id: 'pages.watchlist.history.threshold', defaultMessage: 'Threshold' }),
        body: (record: MatchManyLogSummary) => {
          return (
            <div className={`flex flex-col justify-center items-center ${this.getScoreItem(record.threshold)} min-w-[52px] w-[52px] rounded-[50px] h-[25px]`}>
              {record.threshold}
            </div>
          );
        },
      },
      {
        field: 'maxScore',
        className: 'min-w-[130px]',
        header: LanguageUtil.get({ id: 'pages.watchlist.history.maxScore', defaultMessage: 'Max Score' }),
        body: (record: MatchManyLogSummary) => {
          return (
            <div className={`flex flex-col justify-center items-center ${this.getScoreItem(record.maxScore)} min-w-[52px] w-[52px] rounded-[50px] h-[25px]`}>
              {record.maxScore}
            </div>
          );
        },
      },
      {
        field: 'resultCount',
        className: 'min-w-[130px]',
        header: LanguageUtil.get({ id: 'pages.watchlist.history.resultCount', defaultMessage: 'Result Count' }),
        body: (record: MatchManyLogSummary) => {
          return <div className={'text-xs 2xl:text-md'}>{record.resultCount}</div>;
        },
      },
      {
        field: 'loggedCount',
        className: 'min-w-[130px]',
        header: LanguageUtil.get({ id: 'pages.watchlist.history.loggedCount', defaultMessage: 'Logged Count' }),
        body: (record: MatchManyLogSummary) => {
          return <div className={'text-xs 2xl:text-md'}>{record.loggedCount}</div>;
        },
      },
      {
        field: 'truncated',
        className: 'min-w-[130px]',
        header: LanguageUtil.get({ id: 'pages.watchlist.history.truncated', defaultMessage: 'Truncated' }),
        body: (record: MatchManyLogSummary) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              {record.truncated
                ? LanguageUtil.get({ id: 'common.label.yes', defaultMessage: 'Yes' })
                : LanguageUtil.get({ id: 'common.label.no', defaultMessage: 'No' })}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        className: 'min-w-[300px]',
        header: LanguageUtil.get({ id: 'common.label.createdAt', defaultMessage: 'Created At' }),
        body: (record: MatchManyLogSummary) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={moment(Number(record.createdAt)).format('LLLL')} />
            </div>
          );
        },
      },
    ];
  };

  private getScoreItem = (score: number) => {
    if (score <= 25) {
      return 'bg-[#DBF8E5] text-[#3F9E2C]';
    } else if (score > 25 && score <= 50) {
      return 'bg-[#FCF1BB] text-[#C57500]';
    } else if (score > 50) {
      return 'bg-[#FFF1F3] text-[#D2332F]';
    }
  };
}

interface Props {}

class State {
  logs: MatchManyLogSummary[] = [];
  loading: boolean = true;
  rows = 10;
  totalRecord = 0;
  currentPage = 1;
  filter: Filter = empty();
  count: MatchManyLogCount | undefined;
  settingsVisible: boolean = false;
}

export default WatchlistHistoryComponent;
